import {
  Chart,
  ChartArea,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartLegend,
  ChartSeries,
  ChartSeriesItem,
  ChartTooltip,
  ChartValueAxis,
  ChartValueAxisItem,
  LegendItemVisualArgs,
} from '@progress/kendo-react-charts';
import {
  DEFAULT_LINECHART_OPTIONS,
  useWidgetOptionsContext,
} from 'common/contexts/useWidgetOptionsContext';
import { LineChartData } from 'common/models/chart.model';
import { circleLegentItem, legendProps } from '../widgets/utils';
import { formatAppDate } from 'common/hooks/utils';

export const LINE_CHART_COLORS = [
  '#3EA9E9',
  '#EA7745',
  '#674FC5',
  '#44D991',
  '#305FAB',
  '#D25C86',
  '#28B392',
  '#9E4EC4',
  '#F0AC5D',
  '#6BD8BE',
];

export const LINE_CHART_AXIS_ROTATION = -75;

interface KwelloLineChartProps {
  chartData: LineChartData;
  height?: number | string;
}

function KwelloLineChart({ chartData, height = '100%' }: Readonly<KwelloLineChartProps>) {
  const { kwelloLineChartOptions = DEFAULT_LINECHART_OPTIONS } = useWidgetOptionsContext();
  const { chartType, hideLegend, minMaxGap, maxLabels, yMajorUnit, dateFormat } =
    kwelloLineChartOptions;
  const { categories, series, labels } = chartData;

  const { min, max } = series.reduce(
    (acc, curr) => {
      const seriesMin = Math.min(...curr.data);
      const seriesMax = Math.max(...curr.data);
      return {
        min: Math.min(acc.min, seriesMin),
        max: Math.max(acc.max, seriesMax),
      };
    },
    { min: Infinity, max: -Infinity }
  );

  const decimalPlaces = yMajorUnit ? yMajorUnit.toString().split('.')[1]?.length : 0;

  const flooredMin = decimalPlaces !== undefined ? parseFloat(min.toFixed(decimalPlaces)) : min;
  const flooredMax = decimalPlaces !== undefined ? parseFloat(max.toFixed(decimalPlaces)) : max;

  const minValue = minMaxGap ? flooredMin - minMaxGap : undefined;
  const maxValue = minMaxGap ? flooredMax + minMaxGap : undefined;
  const numberOfLabels = categories.length;
  const categoryAxisLabelStep =
    maxLabels && numberOfLabels > maxLabels ? Math.round(numberOfLabels / maxLabels) : undefined;

  const yAxisLabel = undefined;

  const renderTooltip = (context: any): any => {
    const labelText = labels?.[context.point.series.name] ?? 'value';
    const value = (
      <span>
        Date: {formatAppDate(context.point.category, dateFormat)}
        <br />
        {`${labelText}: ${context.point.value}`}
      </span>
    );
    return value || '0';
  };

  const renderLegendItem = (props: LegendItemVisualArgs) => {
    const labelId = props.series.name;
    const labelText = labels?.[props.series.name] ?? labelId;

    const view = circleLegentItem(props, labelText);

    return view;
  };

  const renderSeriesItems = () => {
    let result = [];
    result = series.map((item: any, index: any) => {
      return (
        <ChartSeriesItem
          style={kwelloLineChartOptions.lineStyle}
          color={LINE_CHART_COLORS[index]}
          missingValues="interpolate"
          key={`${item.name}-${index}`}
          type={chartType}
          markers={{ visible: kwelloLineChartOptions.markers }}
          data={item.data}
          name={item.name}
          line={{
            style: kwelloLineChartOptions.lineStyle,
          }}
        ></ChartSeriesItem>
      );
    });
    result = [
      ...result,
      ...series.map((item: any, index: any) => {
        return (
          <ChartSeriesItem
            style={kwelloLineChartOptions.lineStyle}
            color={LINE_CHART_COLORS[index]}
            visibleInLegend={false}
            key={`Line-${index}`}
            type={chartType}
            markers={{ visible: categories.length === 1 || kwelloLineChartOptions.markers }}
            data={item.data}
            name={item.name}
            line={{
              style: kwelloLineChartOptions.lineStyle,
            }}
          ></ChartSeriesItem>
        );
      }),
    ];
    return result;
  };

  return (
    <Chart transitions={false} style={{ height }} onLegendItemClick={(e) => e.preventDefault()}>
      <ChartArea />
      <ChartLegend {...legendProps} visible={!hideLegend} item={{ visual: renderLegendItem }} />
      <ChartTooltip render={renderTooltip} />
      <ChartValueAxis>
        <ChartValueAxisItem
          min={minValue}
          max={maxValue}
          line={{ visible: false }}
          title={{ text: yAxisLabel, visible: !!yAxisLabel, color: '#fff' }}
          majorUnit={yMajorUnit}
        />
      </ChartValueAxis>
      <ChartCategoryAxis>
        <ChartCategoryAxisItem
          type="category"
          categories={categories}
          majorGridLines={{ visible: false }}
          labels={{
            rotation: LINE_CHART_AXIS_ROTATION,
            step: categoryAxisLabelStep,
            content: (e) => {
              return formatAppDate(e.value, dateFormat);
            },
          }}
        />
      </ChartCategoryAxis>
      <ChartSeries>{renderSeriesItems()}</ChartSeries>
    </Chart>
  );
}

export default KwelloLineChart;
